import { forwardRef, InputHTMLAttributes, ReactNode } from 'react'
import { cn } from '../utilities.ts'
import { cva, VariantProps } from 'class-variance-authority'

export const inputVariants = cva(
  'focus:ring-primary disabled:opacity-70 w-full',
  {
    variants: {
      variant: {
        text: 'block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6',
        number:
          'block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6 pl-6',
        checkbox: 'h-4 w-4 rounded border-gray-300 text-primary',
        radio: 'h-4 w-4 border-gray-300 text-primary focus:ring-primary',
      },
      hasLeftIcon: {
        true: 'pl-8', 
      },
    },
    defaultVariants: {
      variant: 'text',
      hasLeftIcon: false,
    },
  }
)

const SupportedInputTypes = [
  'text',
  'checkbox',
  'number',
  'email',
  'password',
  'tel',
  'file',
  'radio',
]

type InputProps = InputHTMLAttributes<HTMLInputElement> &
  VariantProps<typeof inputVariants> & {
    leftIcon?: ReactNode // New prop for left icon
  }

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, type = 'text', variant, leftIcon, ...props }, ref) => {
    if (!SupportedInputTypes.includes(type ?? ''))
      throw new Error(`${type} is not a supported Custom-Input type`)

    switch (type) {
      case 'email':
      case 'password':
      case 'tel':
      case 'text':
      case 'file':
        variant = 'text'
        break
      case 'number':
        variant = 'number'
        break
      case 'radio':
        variant = 'radio'
        break
      default:
        variant = 'checkbox'
    }

    const hasLeftIcon =
      !!leftIcon &&
      ['text', 'email', 'password', 'tel', 'number'].includes(type)

    // If we have a checkbox or radio, we don't wrap it in a div
    if (type === 'checkbox' || type === 'radio') {
      return (
        <input
          className={cn(inputVariants({ variant, className }))}
          ref={ref}
          type={type}
          {...props}
        />
      )
    }

    // For other input types, we can add the icon
    return (
      <div className="relative w-full">
        {leftIcon && (
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
            {leftIcon}
          </div>
        )}
        <input
          className={cn(inputVariants({ variant, hasLeftIcon, className }))}
          ref={ref}
          type={type}
          {...props}
        />
      </div>
    )
  }
)